import {
    Box,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Typography,
} from '@mui/material'
import { ContentCopy } from '@mui/icons-material'
import { Link } from 'react-router-dom'
import DrawerComponent from 'shared/components/DrawerComponent'
import useToast from 'shared/components/Toast/useToast'

interface UnreachableCandidate {
    id: string
    firstName: string
    lastName: string
    nickname?: string | null
    candidateId: string
    reason: string
}

interface UnreachableCandidatesDrawerProps {
    isOpen: boolean
    unreachableData: UnreachableCandidate[]
    onClose: () => void
}

const UnreachableCandidatesDrawer = ({
    isOpen,
    unreachableData,
    onClose,
}: UnreachableCandidatesDrawerProps) => {
    const toast = useToast()

    const formatName = (candidate: UnreachableCandidate) => {
        const fullName = `${candidate.firstName} ${candidate.lastName}`
        return candidate.nickname
            ? `${fullName} (${candidate.nickname})`
            : fullName
    }

    const copyUnreachableToClipboard = async () => {
        if (unreachableData.length === 0) return

        const textToCopy = unreachableData
            .map(
                (candidate) => `${formatName(candidate)} - ${candidate.reason}`
            )
            .join('\n')

        try {
            await navigator.clipboard.writeText(textToCopy)
            toast({
                title: 'Copied!',
                severity: 'success',
                message: 'Unreachable candidates copied to clipboard',
            })
        } catch (error) {
            toast({
                title: 'Error',
                severity: 'error',
                message: 'Failed to copy to clipboard',
            })
        }
    }

    const unreachableCount = unreachableData.length

    return (
        <DrawerComponent
            open={isOpen}
            anchor="right"
            primaryActionText="Close"
            onPrimaryAction={onClose}
            headerTitle={`Blast Results - ${unreachableCount} Unreachable`}
        >
            <Box sx={{ position: 'relative' }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2,
                    }}
                >
                    <Typography variant="h6">
                        Candidates Not Reached ({unreachableCount})
                    </Typography>
                    <IconButton
                        onClick={copyUnreachableToClipboard}
                        size="small"
                        title="Copy to clipboard"
                    >
                        <ContentCopy />
                    </IconButton>
                </Box>

                <Box
                    sx={{
                        maxHeight: 400,
                        overflow: 'auto',
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                    }}
                >
                    <List dense>
                        {unreachableData.map((candidate) => (
                            <ListItem key={candidate.id}>
                                <ListItemText
                                    primary={
                                        <Link
                                            to={`/people/${candidate.candidateId}`}
                                            target="_blank"
                                            style={{ color: 'inherit' }}
                                        >
                                            {formatName(candidate)}
                                        </Link>
                                    }
                                    secondary={candidate.reason}
                                />
                            </ListItem>
                        ))}
                    </List>
                </Box>
            </Box>
        </DrawerComponent>
    )
}

export default UnreachableCandidatesDrawer
